import React, { useState, useEffect, useContext } from "react";
import ArticleDataService from "../services/ArticleService";
import logo from "./../images/logo.jpg";
import { ApplicationContext } from "./ApplicationContext";
import './ShopComponent.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Credits from "./Credits";
import axiosInstance from '../services/api';
import { Button, Card, Input } from './common';
import { gridStyles } from '../styles/tailwind.classes';
import { toastService } from '../services/toastService';
import { calculatePriceWithDiscount, isInfluencerCustomer } from './utils';


const images = require.context("./../images/", true);

const isNewProduct = (creationTime) => {
  if (!creationTime) return false;
  const now = new Date();
  const productDate = new Date(creationTime);
  const diffTime = Math.abs(now - productDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 45;
};

const ShopComponent = (props) => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [showDiscountedOnly, setShowDiscountedOnly] = useState(false);
  const { loggedInClient, addOrUpdateBasketItem } = useContext(ApplicationContext);
  const [quantities, setQuantities] = useState({});

  console.log('Image directory:', process.env.REACT_APP_IMAGE_DIRECTORY); // Check the value

  const handleQuantityChange = (id, value) => {
    const newValue = Math.max(value, 0);

    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [id]: newValue,
    }));
  };

  useEffect(() => {
    retrieveArticles();
  }, []);

  const retrieveArticles = () => {
    ArticleDataService.getAll()
      .then((response) => {
        const allArticles = response.data;
        const articles = allArticles.filter(article => article.isVisible === true);

        // Sort articles: new products first, then by ID
        articles.sort((a, b) => {
          const aIsNew = isNewProduct(a.creationTime);
          const bIsNew = isNewProduct(b.creationTime);
          
          if (aIsNew && !bIsNew) return -1;
          if (!aIsNew && bIsNew) return 1;
          
          return Number(a.article_id) - Number(b.article_id);
        });

        // Group sorted articles by brandName
        let groups = articles.reduce((acc, article) => {
          const brandName = article.brand.brandName;
          if (!acc[brandName]) {
            acc[brandName] = [];
          }
          acc[brandName].push(article);
          return acc;
        }, {});

        // Flatten the grouped articles
        const sortedArticles = Object.keys(groups)
          .sort()
          .flatMap(brandName => groups[brandName]);

        setArticles(sortedArticles);
        setFilteredArticles(sortedArticles);
        setBrands(Object.keys(groups).sort());
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const addToBasket = (article, quantity) => {
    if (quantity > 0) {
      addOrUpdateBasketItem(article, quantity);
      setQuantities((prevQuantities) => ({
        ...prevQuantities,
        [article.article_id]: "",
      }));
    }
  };

  const brandDiscount = (brand) => {
    const brandName = brand.brandName;
    const discountForTheBrand = loggedInClient?.discounts?.find((item) => item.brand.brandName === brandName);
    if (discountForTheBrand) {
      return discountForTheBrand.discount;
    } else {
      return 0;
    }
  };

  const applyFilters = (brandName, discountOnly) => {
    let filtered = articles;

    // Apply brand filter
    if (brandName) {
      if (brandName === "ACTIVE PHARMA") {
        filtered = filtered.filter(article => article.brand.brandName.startsWith(brandName));
      } else {
        filtered = filtered.filter(article => article.brand.brandName === brandName);
      }
    }

    // Apply discount filter
    if (discountOnly) {
      filtered = filtered.filter(article => article.discountPercentage && article.discountPercentage > 0);
    }

    setFilteredArticles(filtered);
  };

  const handleBrandFilterChange = (brandName) => {
    setSelectedBrand(brandName);
    applyFilters(brandName, showDiscountedOnly);
  };

  const handleDiscountFilterChange = (checked) => {
    setShowDiscountedOnly(checked);
    applyFilters(selectedBrand, checked);
  };

  const numberFormatter = new Intl.NumberFormat("sr-RS", {
    style: "currency",
    currency: "RSD",
  });

  const discountedPrice = (price, discount) => {
    return (Number(price) * (1 - Number(discount / 100)).toFixed(2));
  }

  const updatedArticles = filteredArticles.map((article) => {
    const newWholesalePrice = (Number(article.retailPrice) * (1 - Number(brandDiscount(article.brand)) / 100)).toFixed(2);
    return { ...article, wholesalePrice: newWholesalePrice };
  });

  const handleValueValidation = (id, value, minQuantityDemand) => {
    const parsedValue = parseInt(value) || 0;
    const newValue = Math.max(Math.round(parsedValue / minQuantityDemand) * minQuantityDemand, 0);

    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [id]: newValue,
    }));
  };

  /* PROD ENVIROMENT */
  const findArticleImage = (code) => {
    const baseUrl = "http://virdzinijaportal.com/uploads"; // Hardcoded base URL
    return `${baseUrl}/${code}.png`;
  };
  

  /* TEST ENVIROMENT */
 /*  const findArticleImage = (code) => {
    const checkFileExists = (path) => {
      try {
        images(path);
        return true;
      } catch (error) {
        return false;
      }
    };

    if (checkFileExists(`./${code}.jpg`)) {
      return images(`./${code}.jpg`);
    } else if (checkFileExists(`./${code}.png`)) {
      return images(`./${code}.png`);
    } else {
      return logo;
    }
  };
 */
  const addedBrands = new Set();

  // Reusable component for displaying labeled values
  const LabeledValue = ({ label, value, className = "" }) => (
    <div className={`mb-2 ${className}`}>
      <label className="label-text">{label}</label>
      <div className="value-text">{value}</div>
    </div>
  );

  // Quantity Input Component
  const QuantityInput = ({ article, currentQuantity, onQuantityChange, onBlur }) => (
    <div className="flex items-center space-x-2">
      <button
        className="btn-quantity"
        onClick={() => currentQuantity > 0 && onQuantityChange(currentQuantity - article.minimumQuantityDemand)}
      >
        -
      </button>
      <input
        type="number"
        className="input-base text-center w-20"
        min={article.minimumQuantityDemand}
        value={currentQuantity}
        onChange={(e) => onQuantityChange(e.target.value)}
        onBlur={onBlur}
      />
      <button
        className="btn-quantity"
        onClick={() => onQuantityChange(currentQuantity + article.minimumQuantityDemand)}
      >
        +
      </button>
    </div>
  );

  // Brand Filter Component
  const BrandFilter = ({ selectedBrand, brands, onBrandChange }) => (
    <div className="w-full max-w-xs mb-8">
      <h5 className="text-lg font-semibold mb-2">Filtriranje po brendu</h5>
      <select
        className="filter-select"
        value={selectedBrand}
        onChange={(e) => onBrandChange(e.target.value)}
      >
        <option value="">Svi brendovi</option>
        {brands.map((brandName, index) => {
          if (brandName.toLowerCase().includes("active pharma")) {
            if (addedBrands.has("ACTIVE PHARMA")) {
              return null;
            } else {
              addedBrands.add("ACTIVE PHARMA");
              return (
                <option key={index} value="ACTIVE PHARMA">
                  ACTIVE PHARMA
                </option>
              );
            }
          } else {
            return (
              <option key={index} value={brandName}>
                {brandName}
              </option>
            );
          }
        })}
      </select>
    </div>
  );

  // First, add a memo wrapper for the LazyLoadImage to prevent unnecessary re-renders
  const MemoizedProductImage = React.memo(({ src, alt }) => (
    <div className="w-full h-full flex items-center justify-center">
      <LazyLoadImage
        src={src}
        alt={alt}
        className="object-contain max-w-[90%] max-h-[90%]"
        effect="opacity"
        wrapperClassName="w-full h-full !flex items-center justify-center"
      />
    </div>
  ));

  // Memoize the ProductCard component
  const ProductCard = React.memo(({
    article,
    onAddToBasket,
    initialQuantity = "",
    minimumQuantityDemand,
    customer
  }) => {
    // Move quantity state to component level
    const [quantity, setQuantity] = useState(initialQuantity);

    // Check if customer is an influencer (no discounts)
    const isInfluencer = isInfluencerCustomer(customer);

    // Use new additive discount logic: totalDiscount = articleDiscount% + customerDiscount%
    // For influencers, customer discount is always 0
    const articleDiscountPercent = article.discountPercentage || 0;
    const customerDiscountPercent = isInfluencer ? 0 : brandDiscount(article.brand);
    
    const finalPrice = calculatePriceWithDiscount(
      article.wholesalePrice,
      articleDiscountPercent,
      customerDiscountPercent
    ).toFixed(2);
    
    const hasDiscount = article.discountPercentage && article.discountPercentage > 0;

    const imagePath = React.useMemo(() => findArticleImage(article.code), [article.code]);

    // Local quantity handlers
    const handleQuantityChange = (value) => {
      const newValue = Math.max(value, 0);
      setQuantity(newValue);
    };

    const handleValueValidation = (value) => {
      const parsedValue = parseInt(value) || 0;
      const newValue = Math.max(Math.round(parsedValue / minimumQuantityDemand) * minimumQuantityDemand, 0);
      setQuantity(newValue);
    };

    const handleAddToBasket = () => {
      onAddToBasket(article, quantity);
      setQuantity("");
      toastService.success('Uspešno dodavanje artikla ' + article.name + ' u korpu!');
    };

    return (
      <Card
        variant={article.isActive ? 'default' : 'inactive'}
        className="flex flex-col h-full relative"
      >
        {hasDiscount && (
          <div className="absolute -top-3 -left-3 z-10">
            <div className="akcija-badge bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-1.5 
              rounded-full text-sm font-bold shadow-lg transform -rotate-12 
              border-2 border-white
              flex items-center justify-center">
              <span className="drop-shadow-md">AKCIJA!</span>
            </div>
          </div>
        )}
        
        {isNewProduct(article.creationTime) && (
          <div className="absolute -top-3 -right-3 z-10">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1.5 
              rounded-full text-sm font-bold shadow-lg transform rotate-12 
              animate-pulse border-2 border-white
              flex items-center justify-center">
              <span className="drop-shadow-md">NOVO!</span>
            </div>
          </div>
        )}
        
        <div className="p-4 flex flex-col h-full">
          <div className="h-60 w-full mb-4 bg-white rounded-md">
            <MemoizedProductImage
              src={imagePath}
              alt={article.name}
            />
          </div>

          <div className="flex flex-col flex-grow">
            <div className="flex justify-center mb-3">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {article.code}
              </span>
            </div>
            
            <h3 className="text-center font-semibold text-gray-800 mb-4 min-h-[4.5rem] line-clamp-3 text-sm sm:text-base leading-tight">
              {article.name}
            </h3>

            <div className="hidden md:flex md:flex-col space-y-2 mb-4">
              <div className="text-sm text-gray-600 flex justify-between">
                <span>Transportno Pakovanje:</span>
                <span className="font-medium">{article.quantityPerTransportPackage} KOM</span>
              </div>
              <div className="text-sm text-gray-600 flex justify-between">
                <span>Minimalna Količina:</span>
                <span className="font-medium">{article.minimumQuantityDemand} KOM</span>
              </div>
              <div className="text-sm text-gray-600 flex justify-between items-center">
                <span>Fakturna Cena:</span>
                {hasDiscount ? (
                  <div className="discount-prices">
                    <span className="price-original">{numberFormatter.format(article.wholesalePrice)}</span>
                    <span className="price-discounted">{numberFormatter.format(article.wholesalePrice * (1 - article.discountPercentage / 100))}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 ml-1">
                      -{article.discountPercentage}%
                    </span>
                  </div>
                ) : (
                  <span className="font-medium">{numberFormatter.format(article.wholesalePrice)}</span>
                )}
              </div>
            </div>

              <div className="mt-auto">
              {/* Discount breakdown section */}
              <div className="mb-3 px-2">
                <div className="flex flex-wrap gap-1.5 justify-center items-center">
                  {isInfluencer ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                      Puna cena (bez rabata)
                    </span>
                  ) : (
                    <>
                      {articleDiscountPercent > 0 && (
                        <>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                            <i className="fas fa-tag mr-1"></i>
                            Akcija: {articleDiscountPercent}%
                          </span>
                          <span className="text-gray-400 font-bold">+</span>
                        </>
                      )}
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                        <i className="fas fa-user mr-1"></i>
                        Rabat: {customerDiscountPercent}%
                      </span>
                      {articleDiscountPercent > 0 && (
                        <>
                          <span className="text-gray-400 font-bold">=</span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
                            <i className="fas fa-percentage mr-1"></i>
                            {(articleDiscountPercent + customerDiscountPercent).toFixed(1)}%
                          </span>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Final price */}
              <div className={`text-center mb-4 ${hasDiscount ? 'discount-price-container' : ''}`}>
                <p className="text-xs text-gray-500 mb-1">Finalna cena</p>
                <div className="flex items-center justify-center">
                  <p className="text-lg font-bold text-gray-900">
                    {numberFormatter.format(finalPrice)}
                  </p>
                </div>
              </div>

              {article.isActive ? (
                <div className="space-y-3">
                  <div className="flex justify-center items-center space-x-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleQuantityChange(Math.max((quantity || 0) - minimumQuantityDemand, 0))}
                      className="w-12 h-10 flex items-center justify-center"
                    >
                      -
                    </Button>
                    
                    <Input
                      type="number"
                      value={quantity}
                      onChange={(e) => handleQuantityChange(e.target.value)}
                      onBlur={(e) => handleValueValidation(e.target.value)}
                      className="text-center w-20 h-10"
                    />
                    
                    <Button
                      variant="secondary"
                      onClick={() => handleQuantityChange((quantity || 0) + minimumQuantityDemand)}
                      className="w-12 h-10 flex items-center justify-center"
                    >
                      +
                    </Button>
                  </div>

                  <Button
                    variant="success"
                    disabled={!quantity || quantity % minimumQuantityDemand !== 0}
                    onClick={handleAddToBasket}
                    className="w-full h-10"
                  >
                    Dodaj u korpu
                  </Button>
                </div>
              ) : (
                <div className="bg-red-500 text-white text-center py-2 rounded-md">
                  Nedostupno
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  }, (prevProps, nextProps) => {
    return (
      prevProps.article.article_id === nextProps.article.article_id &&
      prevProps.article.creationTime === nextProps.article.creationTime &&
      prevProps.initialQuantity === nextProps.initialQuantity &&
      prevProps.minimumQuantityDemand === nextProps.minimumQuantityDemand
    );
  });

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col w-full">
        <div className="mb-8">
          <div className="max-w-4xl mx-auto w-full px-4">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Filtriranje artikala
            </h2>
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-end">
              {/* Brand Filter */}
              <div className="w-full md:flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brend
                </label>
                <select
                  value={selectedBrand}
                  onChange={(e) => handleBrandFilterChange(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">Svi brendovi</option>
                  {brands.map((brandName, index) => (
                    <option key={index} value={brandName}>
                      {brandName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Discount Filter */}
              <div className="w-full md:w-auto">
                <label className="flex items-center space-x-3 cursor-pointer group p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={showDiscountedOnly}
                    onChange={(e) => handleDiscountFilterChange(e.target.checked)}
                    className="w-5 h-5 text-red-500 border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:ring-offset-0 cursor-pointer transition-all"
                  />
                  <span className="text-sm font-medium text-gray-700 select-none group-hover:text-red-600 transition-colors flex items-center gap-2">
                    <span className="hidden sm:inline">Artikli na akciji</span>
                    <span className="sm:hidden">Samo akcija</span>
                    <span className="text-red-500 font-bold">🔥</span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className={gridStyles.responsive}>
          {filteredArticles.map((article) => (
            <ProductCard
              key={article.article_id}
              article={article}
              onAddToBasket={addToBasket}
              minimumQuantityDemand={article.minimumQuantityDemand}
              customer={loggedInClient}
            />
          ))}
        </div>

        <Credits className="mt-8" />
      </div>
    </div>
  );
};

export default ShopComponent;
